import React, { useEffect, useState } from 'react';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { CARRIER_LABELS, DIRECTIONS, SHIPMENT_STATUSES, SORT_OPTIONS } from '../../constants/shipments';
import { activeFilterCount } from './useShipmentFilters';
import { SelectField, TextField } from './FormFields';
import MultiSelect from './MultiSelect';
import EntityPicker from './EntityPicker';

const DEBOUNCE_MS = 300;
const OPEN_KEY = 'erp-shipments-filters-open';
const STATUS_OPTIONS = SHIPMENT_STATUSES.map((s) => ({ value: s.value, label: s.label }));
const ORDER_OPTIONS = [{ value: 'desc', label: 'Sestupně' }, { value: 'asc', label: 'Vzestupně' }];

// Remembering the open / closed choice is only a per-user convenience: it must work when storage is unavailable
const readOpen = () => { try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; } };
const writeOpen = (open) => { try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* storage blocked: ignore */ } };

// Narrowing filters that live in the collapsible panel (the search box and the summary chips are always visible)
const panelCount = (f) => [f.status.length, f.carrier.length, f.direction, f.customerId, f.supplierId, f.from, f.to].filter(Boolean).length;

// Search box (300 ms debounce, written to the URL with replace) + a "Filtry (n)" toggle + "Vymazat".
// The panel (status, carrier, direction, customer, supplier, date range, sorting) starts collapsed unless a filter is
// already active in the URL or the user left it open last time.
const ShipmentFilters = ({ filters, setFilters, clearFilters, carriers }) => {
  const inPanel = panelCount(filters);
  const total = activeFilterCount(filters);
  const [text, setText] = useState(filters.q);
  const [prevQ, setPrevQ] = useState(filters.q);
  const [open, setOpen] = useState(() => readOpen() || inPanel > 0);
  const [prevPanel, setPrevPanel] = useState(inPanel);

  // the URL changed from outside (back button, "Vymazat"): follow it
  if (filters.q !== prevQ) {
    setPrevQ(filters.q);
    setText(filters.q);
  }
  // a panel filter appeared from outside (back button) while collapsed: show it
  if (inPanel !== prevPanel) {
    setPrevPanel(inPanel);
    if (inPanel > prevPanel && !open) setOpen(true);
  }

  useEffect(() => {
    if (text === filters.q) return undefined;
    const timer = setTimeout(() => setFilters({ q: text.trim() }, { replace: true }), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text, filters.q, setFilters]);

  const carrierOptions = (carriers?.length ? carriers.map((c) => ({ value: c.code, label: c.name || CARRIER_LABELS[c.code] || c.code }))
    : Object.entries(CARRIER_LABELS).map(([value, label]) => ({ value, label })));

  const toggle = () => {
    const next = !open;
    setOpen(next);
    writeOpen(next);
  };

  return (
    <section className="shp-card shp-filters" aria-label="Filtry a vyhledávání">
      <div className="shp-filters__search">
        <div>
          <Search size={18} className="shp-search-icon" aria-hidden="true" />
          <label htmlFor="shp-search" className="shp-sr">Hledat zásilku</label>
          <input
            id="shp-search" type="search" className="shp-input" value={text} maxLength={100} autoComplete="off"
            placeholder="Číslo zásilky, objednávka, popis, odesílatel…" onChange={(e) => setText(e.target.value)}
          />
        </div>
        <button type="button" className="shp-textbtn shp-filters__toggle" aria-expanded={open} aria-controls="shp-filter-panel" onClick={toggle}>
          <SlidersHorizontal size={16} aria-hidden="true" /> Filtry
          {inPanel > 0 && <span className="shp-count"><span aria-hidden="true">{inPanel}</span><span className="shp-sr"> aktivních filtrů: {inPanel}</span></span>}
        </button>
        {total > 0 && (
          <button type="button" className="shp-textbtn" onClick={clearFilters}><X size={15} aria-hidden="true" /> Vymazat</button>
        )}
      </div>

      <div id="shp-filter-panel" className="shp-filters__panel" data-open={open}>
        <div className="shp-filters__row">
          <MultiSelect label="Stav" options={STATUS_OPTIONS} value={filters.status} onChange={(upd) => setFilters((f) => ({ status: upd(f.status) }))} />
          <MultiSelect label="Dopravce" options={carrierOptions} value={filters.carrier} onChange={(upd) => setFilters((f) => ({ carrier: upd(f.carrier) }))} />
          <SelectField label="Směr" value={filters.direction} onChange={(e) => setFilters({ direction: e.target.value })} placeholder="Všechny" options={DIRECTIONS} />
          <EntityPicker kind="customer" label="Zákazník" value={filters.customerId} onChange={(id) => setFilters({ customerId: id })} placeholder="Všichni zákazníci" />
          <EntityPicker kind="supplier" label="Dodavatel" value={filters.supplierId} onChange={(id) => setFilters({ supplierId: id })} placeholder="Všichni dodavatelé" />
          <TextField label="Poslední událost od" type="date" value={filters.from} max={filters.to || undefined} onChange={(e) => setFilters({ from: e.target.value })} />
          <TextField label="Poslední událost do" type="date" value={filters.to} min={filters.from || undefined} onChange={(e) => setFilters({ to: e.target.value })} />
          <SelectField label="Řadit podle" value={filters.sort || 'lastEvent'} onChange={(e) => setFilters({ sort: e.target.value === 'lastEvent' ? '' : e.target.value })} options={SORT_OPTIONS} />
          <SelectField label="Pořadí" value={filters.order || 'desc'} onChange={(e) => setFilters({ order: e.target.value === 'desc' ? '' : e.target.value })} options={ORDER_OPTIONS} />
        </div>
      </div>
    </section>
  );
};

export default ShipmentFilters;
